import { IntlShape } from 'react-intl';
import makeDicomFromAcquisitionDetails from 'app/utils/dicom/makeDicomFromAcquisitionDetails';
import { Patient } from 'app/interfaces/Patient';
import IDicomBuilder, { EncodedTransferSyntaxes } from 'app/interfaces/IDicomBuilder';
import { convertCropAnnotationsToCropRect } from 'app/utils/image/convertCropAnnotationsToCropRect';
import { DisplayableImageData, XRayImage } from 'app/interfaces/Image';

export type DicomExportableImage = Omit<
  XRayImage,
  'predictions' | 'image_metadata' | 'feedback' | 'backendId'
> & { displayableImage: DisplayableImageData };

type ImageInfo = {
  studyId: string;
  image: DicomExportableImage;
  patient: Patient;
  imagesOrder: string[];
  transferSyntax?: EncodedTransferSyntaxes;
  forceUniqueID?: boolean;
};

type MakeDicomImageFromDetailsAndViewportDependencies = {
  intl: IntlShape;
  dicomBuilder: IDicomBuilder;
  clinicInfo?: Record<string, any> | null;
};

export default function makeDicomImageFromDetailsAndViewport(
  { studyId, image, patient, imagesOrder, transferSyntax, forceUniqueID = false }: ImageInfo,
  { intl, dicomBuilder, clinicInfo }: MakeDicomImageFromDetailsAndViewportDependencies
) {
  const { displayableImage, acquisitionTime, operators } = image;
  const { anatomicRegion, detectorInfo, viewport, annotations, acquisitionConstants, _id } = image;

  const dicomData = makeDicomFromAcquisitionDetails({
    intl,
    studyId,
    imageId: _id,
    imageIndex: imagesOrder.indexOf(_id),
    acquisitionTime,
    acquisitionConstants,
    anatomicRegion,
    detectorInfo,
    patient,
    dicomData: image.dicomData,
    forceUniqueID,
    clinicInfo,
    operators,
  });

  let crop;
  const cropRect = convertCropAnnotationsToCropRect(annotations?.Crop);
  if (cropRect) {
    const [x, y, width, height] = cropRect;
    crop = { x, y, width, height };
  }

  const { invert, rotation, hflip, vflip, voi } = viewport ?? {};

  return dicomBuilder.injectPixelDataFromDisplayableImage(
    dicomData,
    displayableImage,
    { invert, crop, hflip, vflip, rotation },
    voi,
    transferSyntax,
    { annotations }
  );
}
